// The ONE place the app calls the global fetch() for an API request, and the
// one place X-Interface-Language is attached.
//
// The header goes ONLY to our API, decided by where the request actually
// resolves, never by which helper made it:
//   - VITE_I18N is on, and
//   - the interface language is not English, and
//   - the resolved origin is the configured API's (apiUrl('/')), OR the
//     request is same-origin under `vite dev` to a path the dev proxy
//     forwards to the API (src/api/apiPaths.ts).
// Never to Supabase, never to our own Vercel functions under /api/, never to
// any other origin. Flag off (or in English) the call is exactly the fetch()
// it replaces, with the same arguments.
//
// eslint.i18n.config.js bans the bare fetch global everywhere else in src/,
// so a new API call cannot skip this by accident.
import { apiUrl } from '../api/apiUrl'
import { hasForeignAccountTransition, isAccountFenced, waitForAccountTransition, whenUnfenced } from '../lib/accountFence'
import { API_PATHS } from '../api/apiPaths'
import { beginApiRequest, reportApiFailure, reportApiStatus } from '../lib/offline/connectivityFeed'
import { i18nEnabled } from './flag'
import { activeLanguage } from './index'

export const INTERFACE_LANGUAGE_HEADER = 'X-Interface-Language'

function originOf(url: string | undefined): string | null {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

function isDevProxyPath(pathname: string): boolean {
  return API_PATHS.some(p => pathname === p || pathname.startsWith(`${p}/`))
}

/** Whether a request to `pathOrUrl` is an API request that should carry the header. */
export function shouldSendInterfaceLanguage(pathOrUrl: string): boolean {
  if (!i18nEnabled() || activeLanguage() === 'en') return false
  return isApiDestination(pathOrUrl)
}

/**
 * Whether `pathOrUrl` resolves to our API: the configured API origin, or a
 * same-origin dev-proxy path under `vite dev`. Never Supabase, never our own
 * /api/ functions, never any other origin. Also the gate for attaching the
 * Supabase bearer to collection writes (lib/accessToken).
 */
export function isApiDestination(pathOrUrl: string): boolean {
  if (typeof location === 'undefined') return false
  let target: URL
  try {
    target = new URL(pathOrUrl, location.origin)
  } catch {
    return false
  }
  // Our own Vercel functions and Supabase are never the API, whatever the
  // configured API URL happens to be.
  if (target.origin === location.origin && target.pathname.startsWith('/api/')) return false
  if (target.origin === originOf(import.meta.env.VITE_SUPABASE_URL)) return false

  if (target.origin === originOf(apiUrl('/'))) return true
  return target.origin === location.origin && import.meta.env.DEV === true && isDevProxyPath(target.pathname)
}

/**
 * fetch() for API requests: identical, plus X-Interface-Language when it
 * applies. Held while a peer tab signs this tab's account out
 * (src/lib/accountChannel.ts), so nothing is sent for the departing account.
 */
export function apiFetch(pathOrUrl: string, init?: RequestInit): Promise<Response> {
  if (isAccountFenced()) return whenUnfenced().then(() => apiFetch(pathOrUrl, init))
  if (hasForeignAccountTransition()) return waitForAccountTransition().then(() => apiFetch(pathOrUrl, init))
  // Offline contract C1: every API request reports whether the server
  // answered (a 4xx is an answer), with the path epoch it started under.
  if (!isApiDestination(pathOrUrl)) return send(pathOrUrl, init)
  const epoch = beginApiRequest()
  return send(pathOrUrl, init).then(
    (res) => { reportApiStatus(res.status, epoch); return res },
    (err: unknown) => {
      // The caller's own abort says nothing about the server.
      if (!init?.signal?.aborted) reportApiFailure(epoch)
      throw err
    },
  )
}

/**
 * The C1 probe (GET /health/live): an API request whose result the
 * connectivity monitor reads itself, so it is not reported to the feed. Not
 * held by the account fence (it carries nothing of any account).
 */
export function apiFetchUnreported(pathOrUrl: string, init?: RequestInit): Promise<Response> {
  return send(pathOrUrl, init)
}

function send(pathOrUrl: string, init?: RequestInit): Promise<Response> {
  if (!shouldSendInterfaceLanguage(pathOrUrl)) {
    return init === undefined ? fetch(pathOrUrl) : fetch(pathOrUrl, init)
  }
  const headers = new Headers(init?.headers)
  headers.set(INTERFACE_LANGUAGE_HEADER, activeLanguage())
  // The destination check above sees only the FIRST hop. On a cross-origin
  // redirect the browser drops Authorization but may forward a custom header
  // like this one (Fetch Standard, HTTP-redirect fetch), so a header-bearing
  // request refuses to follow redirects at all. The API redirects only on
  // its /stores/ affiliate routes, which the app opens as links, never
  // fetches (pinned by apiFetch.test.ts). An explicit caller choice wins.
  // eslint-disable-next-line i18next/no-literal-string -- fetch option
  return fetch(pathOrUrl, { ...init, headers, redirect: init?.redirect ?? 'error' })
}
